import { Button } from '@mui/material';
import { validateNotNullOrUndefined } from '@weft/utils';
import React, { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useCreatePlanContext } from '../../../../contexts/create-plan-context';
import { useFormErrorNotificationContext } from '../../../../contexts/form-error-notification-context/form-error-notification-context';
import { useVyNavigate } from '../../../../hooks/use-vy-navigate';
import type { StepsCtaLabels } from '../create-plan-page-consts/create-plan-default-values';
import { CREATE_PLAN_DEFAULT_VALUES } from '../create-plan-page-consts/create-plan-default-values';
import type { PlanInputForm } from './CreatePlanForm/create-plan-form-types/plan-input-form';
import type { CreatePlanTab } from './CreatePlanPageTabs/create-plan-page-types/create-plan-tab';

type StepperNavigatorProps = {
  stepsLabels: StepsCtaLabels,
  formClearable: boolean,
  skipSettingsStep?: boolean,
};

export const CreatePlanPageStepperNavigator: React.FC<StepperNavigatorProps> = ({ stepsLabels, formClearable, skipSettingsStep }) => {
  const { planCreationStepper: { currentStep, nextStep, onFinalStep, isFinalStep } } = useCreatePlanContext();
  const { setShowErrorNotification } = useFormErrorNotificationContext<PlanInputForm, CreatePlanTab>();
  const { trigger, reset, resetField } = useFormContext();
  const navigate = useVyNavigate();
  
  validateNotNullOrUndefined(onFinalStep);

  const handleClick = useCallback(async () => {
    if (isFinalStep || skipSettingsStep) {
      return onFinalStep();
    }

    const validForm = await trigger();
    if (!validForm) {
      setShowErrorNotification(true);

      return undefined;
    }

    return nextStep();
  }, [isFinalStep, skipSettingsStep, trigger, nextStep, onFinalStep, setShowErrorNotification]);

  const handleClearForm = useCallback(() => {
    reset(CREATE_PLAN_DEFAULT_VALUES, {
      keepDefaultValues: true,
    });
    resetField('name');
    navigate('/plans/create');
  }, [reset, resetField, navigate]);

  return (
    <div className="flex justify-end gap-x-4 border-t border-t-[#DFEBF1] px-6 py-4">
      {formClearable && (
        <Button
          variant="text"
          color="primary"
          size="medium"
          disableElevation
          className="rounded-lg text-sm font-medium disabled:bg-[#EAEEFC] disabled:text-[#A3C8FF]"
          onClick={handleClearForm}
        >
          Clear form
        </Button>
      )}

      <Button
        variant="contained"
        color="primary"
        size="medium"
        disableElevation
        className="rounded-lg text-sm font-medium disabled:bg-[#EAEEFC] disabled:text-[#A3C8FF]"
        onClick={handleClick}
      >
        {stepsLabels[currentStep]}
      </Button>
    </div>
  );
};